export type StatusFormKind = "generic" | "scorm12" | "scorm2004" | "xapi";

export type StatusFormFieldKey =
  | "currentStatus"
  | "newStatus"
  | "lessonLocationValue"
  | "suspendDateValue"
  | "completedInsteadOfPassed"
  | "state"
  | "completionDate"
  | "score"
  | "reason";

export type StatusFormValues = Partial<
  Record<StatusFormFieldKey, string | null>
>;

export type StatusFormErrors = Partial<Record<StatusFormFieldKey, boolean>>;

export interface StatusFormField {
  key: StatusFormFieldKey;
  type: "text" | "select" | "date";
  options?: "status" | "newStatus" | "completedInsteadOfPassed";
  labelKey?: (values: StatusFormValues) => string | undefined;
  format?: "number";
  resetOnChangeOf?: StatusFormFieldKey;
  fullWidth?: boolean;
  readOnly?: boolean;
  isEnabled?: (values: StatusFormValues) => boolean;
  isRequired?: (values: StatusFormValues) => boolean;
}

export const COMPLETED = "C";
export const EXEMPT = "X";
export const TRACKING_COMPLETED = "COMPLETED";
export const TRACKING_PASSED = "PASSED";

export const NEW_STATUS_OPTIONS_BY_KIND: Record<StatusFormKind, string[]> = {
  generic: [COMPLETED, EXEMPT],
  scorm12: [TRACKING_COMPLETED, TRACKING_PASSED],
  scorm2004: [TRACKING_COMPLETED],
  xapi: [TRACKING_COMPLETED, TRACKING_PASSED],
};

const isCompleting = (values: StatusFormValues): boolean =>
  values.newStatus === COMPLETED ||
  values.newStatus === TRACKING_COMPLETED ||
  values.newStatus === TRACKING_PASSED;

const isExempting = (values: StatusFormValues): boolean =>
  values.newStatus === EXEMPT;

const isCurrentlyExempt = (values: StatusFormValues): boolean =>
  values.currentStatus === EXEMPT || values.currentStatus === "EXEMPTED";

const CURRENT_STATUS: StatusFormField = {
  key: "currentStatus",
  type: "select",
  options: "status",
  readOnly: true,
};

const NEW_STATUS: StatusFormField = {
  key: "newStatus",
  type: "select",
  options: "newStatus",
  isRequired: () => true,
};

const TRACKING_NEW_STATUS: StatusFormField = {
  key: "newStatus",
  type: "select",
  options: "newStatus",
};

const COMPLETION_DATE: StatusFormField = {
  key: "completionDate",
  type: "date",
  isEnabled: isCompleting,
  isRequired: isCompleting,
};

const GENERIC_COMPLETION_DATE: StatusFormField = {
  key: "completionDate",
  type: "date",
  labelKey: (values) =>
    isExempting(values) || (!values.newStatus && isCurrentlyExempt(values))
      ? "exemptionDate"
      : undefined,
  resetOnChangeOf: "newStatus",
  isEnabled: (values) => isCompleting(values) || isExempting(values),
  isRequired: (values) => isCompleting(values) || isExempting(values),
};

const GENERIC_SCORE: StatusFormField = {
  key: "score",
  type: "text",
  format: "number",
  isEnabled: isCompleting,
};

const GENERIC_REASON: StatusFormField = {
  key: "reason",
  type: "text",
  fullWidth: true,
  isRequired: () => true,
};

const SCORE: StatusFormField = {
  key: "score",
  type: "text",
  format: "number",
  isEnabled: isCompleting,
};

const REASON: StatusFormField = {
  key: "reason",
  type: "text",
  fullWidth: true,
  isRequired: () => true,
};

const SCORM_FIELDS: StatusFormField[] = [
  { key: "lessonLocationValue", type: "text" },
  { key: "suspendDateValue", type: "text" },
];

const KIND_FIELDS: Record<StatusFormKind, StatusFormField[]> = {
  generic: [],
  scorm12: [TRACKING_NEW_STATUS, ...SCORM_FIELDS],
  scorm2004: [TRACKING_NEW_STATUS, ...SCORM_FIELDS],
  xapi: [],
};

export const XAPI_REASON: StatusFormField = {
  key: "reason",
  type: "text",
  fullWidth: true,
  isRequired: () => true,
};

interface NewStatusOptionsContext {
  hasExemptSelected?: boolean;
}

export const getNewStatusOptions = (
  kind: StatusFormKind,
  values: StatusFormValues,
  { hasExemptSelected = false }: NewStatusOptionsContext = {},
): string[] =>
  kind === "generic" && (hasExemptSelected || isCurrentlyExempt(values))
    ? [COMPLETED]
    : NEW_STATUS_OPTIONS_BY_KIND[kind];

interface StatusFormFieldsOptions {
  withScore?: boolean;
}

export const getStatusFormFields = (
  kind: StatusFormKind,
  { withScore = false }: StatusFormFieldsOptions = {},
): StatusFormField[] =>
  kind === "generic"
    ? [
        CURRENT_STATUS,
        NEW_STATUS,
        GENERIC_COMPLETION_DATE,
        ...(withScore ? [GENERIC_SCORE] : []),
        GENERIC_REASON,
      ]
    : kind === "xapi"
      ? [TRACKING_NEW_STATUS, SCORE, COMPLETION_DATE]
      : [CURRENT_STATUS, ...KIND_FIELDS[kind], COMPLETION_DATE, SCORE, REASON];

export const isFieldEnabled = (
  field: StatusFormField,
  values: StatusFormValues,
): boolean => !field.readOnly && (field.isEnabled?.(values) ?? true);

export const isFieldRequired = (
  field: StatusFormField,
  values: StatusFormValues,
): boolean =>
  isFieldEnabled(field, values) && (field.isRequired?.(values) ?? false);

const DECIMAL_PATTERN = /^-?\d+([.,]\d+)?$/;

export const parseDecimal = (value?: string | null): number | null => {
  const text = (value ?? "").trim();
  return DECIMAL_PATTERN.test(text) ? Number(text.replace(",", ".")) : null;
};

const isEmptyValue = (value?: string | null): boolean =>
  value === undefined || value === null || value.trim() === "";

export const isFieldInvalid = (
  field: StatusFormField,
  values: StatusFormValues,
): boolean =>
  field.format === "number" &&
  isFieldEnabled(field, values) &&
  !isEmptyValue(values[field.key]) &&
  parseDecimal(values[field.key]) === null;

export const validateStatusForm = (
  fields: StatusFormField[],
  values: StatusFormValues,
): StatusFormErrors =>
  fields.reduce<StatusFormErrors>((errors, field) => {
    const isMissing =
      isFieldRequired(field, values) && isEmptyValue(values[field.key]);
    return isMissing || isFieldInvalid(field, values)
      ? { ...errors, [field.key]: true }
      : errors;
  }, {});

export const clearDisabledValues = (
  fields: StatusFormField[],
  values: StatusFormValues,
  initialValues: StatusFormValues,
  changedKey?: StatusFormFieldKey,
): StatusFormValues =>
  fields.reduce<StatusFormValues>((next, field) => {
    if (field.readOnly) return next;
    if (!isFieldEnabled(field, values)) {
      return { ...next, [field.key]: initialValues[field.key] ?? null };
    }
    if (changedKey && field.resetOnChangeOf === changedKey) {
      const keepInitial = field.key === "completionDate" && isExempting(values);
      return {
        ...next,
        [field.key]: keepInitial ? (initialValues[field.key] ?? null) : null,
      };
    }
    return next;
  }, values);
